/**
 * Softphone diagnostics panel access.
 *
 * The setting is shown only when the diagnostics feature flag is on and the
 * user has the new softphone enabled. Access to the panel requires that same
 * visibility plus the persisted per-user toggle.
 */

import type {
  SoftphoneDiagnosticsAccess,
  SoftphoneDiagnosticsAccessInput,
  UseSoftphoneDiagnosticsAccessReturn,
} from './index.types';
import { COMMUNICATOR_SOFTPHONE_DIAGNOSTICS } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MobXProviderContext } from 'mobx-react';
import { useSoftphoneCoordinatorStore } from 'Modules/softphone/store';
import { useContext } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';

export type {
  SoftphoneDiagnosticsAccess,
  SoftphoneDiagnosticsAccessInput,
  UseSoftphoneDiagnosticsAccessReturn,
} from './index.types';

export const resolveSoftphoneDiagnosticsAccess = ({
  isFeatureFlagEnabled,
  isNewSoftphoneEnabled,
  isDiagnosticsPanelEnabled,
}: SoftphoneDiagnosticsAccessInput): SoftphoneDiagnosticsAccess => {
  const isSettingVisible =
    isFeatureFlagEnabled === true && isNewSoftphoneEnabled;

  return {
    isSettingVisible,
    hasAccess: isSettingVisible && isDiagnosticsPanelEnabled,
  };
};

export const useSoftphoneDiagnosticsAccess =
  (): UseSoftphoneDiagnosticsAccessReturn => {
    const { person } = useContext<RootStoreProps>(MobXProviderContext);
    const personId = person?.loggedInPersonId;

    const {
      [COMMUNICATOR_SOFTPHONE_DIAGNOSTICS]: isFeatureFlagEnabled,
    } = useFeatureFlags<boolean>([COMMUNICATOR_SOFTPHONE_DIAGNOSTICS]);

    const coordinatorStore = useSoftphoneCoordinatorStore(personId);
    const isNewSoftphoneEnabled = coordinatorStore(
      (state) => state.isNewSoftphoneEnabled
    );
    const isDiagnosticsPanelEnabled = coordinatorStore(
      (state) => state.isDiagnosticsPanelEnabled
    );
    const toggleDiagnosticsPanel = coordinatorStore(
      (state) => state.toggleDiagnosticsPanel
    );

    const { isSettingVisible, hasAccess } = resolveSoftphoneDiagnosticsAccess({
      isFeatureFlagEnabled,
      isNewSoftphoneEnabled,
      isDiagnosticsPanelEnabled,
    });

    return {
      isSettingVisible,
      hasAccess,
      isDiagnosticsPanelEnabled,
      toggleDiagnosticsPanel,
    };
  };
